import { Check } from "lucide-react";
import { isOpen, longDate, timeRange, usd } from "@/lib/training";

const REASON = { full: "Full", closed: "Not available", past: "Completed" };

function Radio({ on }) {
  return <span className={`mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full border-2 shrink-0 ${on ? "bg-white border-white text-[#0B0F19]" : "border-white/30"}`}>{on && <Check size={12} strokeWidth={3.5} />}</span>;
}

// Option A (full program) / Option B (single-class drop-in) for one four-week program, plus the drop-in date picker.
export default function RegistrationOptions({ o, option, sessionId, onChange }) {
  const sched = o.dropin_schedule || [];
  const fullOk = o.full_enabled !== false;
  const dropOk = sched.length > 0;
  const cutoff = o.dropin_cutoff_hours ?? 24;
  const card = (on, dis) => `w-full text-left rounded-2xl border-2 p-4 md:p-5 flex items-start gap-4 transition-colors ${on ? "border-white bg-white/[0.06]" : "border-white/10 hover:border-white/30"} ${dis ? "opacity-50" : ""}`;
  return (
    <div data-testid="registration-options" className="space-y-3">
      {fullOk && (
        <button type="button" data-testid="option-full" aria-pressed={option === "full"} onClick={() => onChange({ option: "full", session: "" })} className={card(option === "full")}>
          <Radio on={option === "full"} />
          <span className="flex-1 min-w-0">
            <span className="block text-lg font-extrabold tracking-tight">Full 4-Week Program</span>
            <span className="mt-1 block text-sm font-medium text-white/60">All {(o.session_dates || []).length} sessions · pay once</span>
            {!isOpen(o) && <span data-testid="option-full-status" className="mt-1 block text-xs font-bold uppercase tracking-wider text-amber-300">{o.display_status === "full" ? "Full" : "Full-program registration closed"}</span>}
            <ol data-testid="option-full-dates" className="mt-2 space-y-0.5 text-sm font-medium text-white/75">{(o.session_dates || []).map((d, i) => <li key={d}><span className="text-white/35 mr-2">{i + 1}.</span>{longDate(d)} · {timeRange(o)}</li>)}</ol>
          </span>
          <span data-testid="option-full-price" className="text-2xl font-extrabold tracking-tight shrink-0">{usd(o.price)}</span>
        </button>
      )}
      {dropOk && (
        <div className={`rounded-2xl border-2 transition-colors ${option === "dropin" ? "border-white bg-white/[0.06]" : "border-white/10 hover:border-white/30"}`}>
          <button type="button" data-testid="option-dropin" aria-pressed={option === "dropin"} onClick={() => onChange({ option: "dropin" })} className="w-full text-left p-4 md:p-5 flex items-start gap-4">
            <Radio on={option === "dropin"} />
            <span className="flex-1 min-w-0">
              <span className="block text-lg font-extrabold tracking-tight">Single-Class Drop-In</span>
              <span className="mt-1 block text-sm font-medium text-white/60">One class of your choice · no four-week commitment · book at least {cutoff} hours ahead</span>
            </span>
            <span data-testid="option-dropin-price" className="text-2xl font-extrabold tracking-tight shrink-0">{usd(o.dropin_price)}</span>
          </button>
          {option === "dropin" && (
            <div data-testid="dropin-dates" className="px-4 md:px-5 pb-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {sched.map((s) => {
                const ok = s.status === "available";
                const on = sessionId === s.id;
                return (
                  <button key={s.id} type="button" disabled={!ok} data-testid={`dropin-date-${s.date}`} aria-pressed={on} onClick={() => onChange({ session: s.id })}
                    className={`text-left rounded-xl border px-4 py-3 transition-colors ${on ? "border-white bg-white text-[#0B0F19]" : ok ? "border-white/20 hover:border-white/50" : "border-white/10 text-white/55 cursor-not-allowed"}`}>
                    <span className="block text-sm font-bold">{longDate(s.date)}</span>
                    <span className={`block text-xs font-semibold ${on ? "text-slate-500" : ok ? "text-white/50" : "text-white/60"}`}>{timeRange(o)} · {ok ? `${s.remaining} spot${s.remaining === 1 ? "" : "s"} left` : s.status === "cutoff" ? `Closed — under ${cutoff} hrs` : REASON[s.status]}</span>
                  </button>
                );
              })}
              {!sched.some((s) => s.status === "available") && <p data-testid="dropin-none" className="sm:col-span-2 text-sm font-semibold text-white/60">No drop-in dates are available for this program right now.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function dropinOffering(o, s) {
  return { ...o, id: `${o.id}:${s.id}`, base_id: o.id, dropin_session_id: s.id, format: "dropin", price: o.dropin_price, session_dates: [s.date],
    start_date: s.date, end_date: s.date, remaining: s.remaining, display_status: s.remaining <= 2 ? "limited" : "open", waitlist_enabled: false };
}
